import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { toDdMmYyyy } from "../../utils/date";

const labels: Record<string, string> = {
  R: "Realistic",
  I: "Investigative",
  A: "Artistic",
  S: "Social",
  E: "Enterprising",
  C: "Conventional",
};
const recommendations: Record<
  string,
  Record<"fields" | "careers" | "courses", string[]>
> = {
  R: {
    fields: [
      "Engineering",
      "Architecture",
      "Veterinary Science",
      "Agriculture",
      "Construction Management",
    ],
    careers: [
      "Civil Engineer",
      "Mechanical Engineer",
      "Architect",
      "Pilot",
      "Quantity Surveyor",
    ],
    courses: [
      "BEng Civil Engineering",
      "BEng Mechanical Engineering",
      "Bachelor of Architecture",
      "Diploma in Electrical Technology",
    ],
  },
  I: {
    fields: [
      "Medicine",
      "Biotechnology",
      "Computer Science",
      "Data Science",
      "Pharmacy",
      "Environmental Science",
    ],
    careers: [
      "Medical Doctor",
      "Researcher",
      "Data Scientist",
      "Pharmacist",
      "Software Engineer",
      "Biochemist",
    ],
    courses: [
      "MBBS Medicine",
      "BSc Biotechnology",
      "BSc Computer Science",
      "BSc Pharmacy",
      "BSc Data Analytics",
    ],
  },
  A: {
    fields: [
      "Graphic Design",
      "Interior Design",
      "Mass Communication",
      "Film & Media",
      "Fine Arts",
      "Animation",
    ],
    careers: [
      "Graphic Designer",
      "UX/UI Designer",
      "Filmmaker",
      "Journalist",
      "Animator",
      "Fashion Designer",
    ],
    courses: [
      "BA Graphic Design",
      "BA Mass Communication",
      "BA Interior Design",
      "BA Animation & Digital Media",
    ],
  },
  S: {
    fields: [
      "Education",
      "Psychology",
      "Counselling",
      "Nursing",
      "Social Work",
      "Public Health",
    ],
    careers: [
      "Teacher",
      "Psychologist",
      "Counsellor",
      "Nurse",
      "HR Manager",
      "Social Worker",
    ],
    courses: [
      "B.Ed. TESL",
      "BA Psychology",
      "BSc Nursing",
      "BA Social Work",
      "BBA Human Resource Management",
    ],
  },
  E: {
    fields: [
      "Business Management",
      "Marketing",
      "Law",
      "Entrepreneurship",
      "International Business",
      "Event Management",
    ],
    careers: [
      "Business Manager",
      "Marketing Executive",
      "Lawyer",
      "Entrepreneur",
      "Sales Director",
      "Event Planner",
    ],
    courses: [
      "BBA Business Management",
      "BA Marketing",
      "LLB Law",
      "BBA International Business",
      "Diploma Hotel Management",
    ],
  },
  C: {
    fields: [
      "Accounting",
      "Finance",
      "Actuarial Science",
      "Information Systems",
      "Banking",
      "Economics",
    ],
    careers: [
      "Accountant",
      "Auditor",
      "Financial Analyst",
      "Actuary",
      "Bank Officer",
      "Economist",
    ],
    courses: [
      "B.Acc Accounting",
      "BSc Actuarial Science",
      "BSc Finance",
      "BSc Information Systems",
      "BA Economics",
    ],
  },
};
const unique = (types: string[], key: "fields" | "careers" | "courses") => [
  ...new Set(types.flatMap((type) => recommendations[type]?.[key] || [])),
];
const List = ({ items }: { items: unknown }) => {
  const values = Array.isArray(items)
    ? items.filter(Boolean).map(String)
    : typeof items === "string"
      ? items
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];
  return values.length ? (
    <ul className="mt-1 grid grid-cols-2 list-disc space-y-1 pl-5">
      {values.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  ) : (
    <p>-</p>
  );
};

const CareerTestSection = ({ lead }: { lead: Record<string, any> }) => {
  const [expanded, setExpanded] = useState(false);
  const result = lead.careerTestResult;
  if (!result) return null;
  const types: string[] = result.topTypes || [];
  const suggested = {
    fields: result.recommendations?.fields || unique(types, "fields"),
    courses: result.recommendations?.courses || unique(types, "courses"),
    careers: result.recommendations?.careers || unique(types, "careers"),
  };
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center justify-between text-left font-bold text-emerald-800 cursor-pointer"
      >
        <span>Career Test</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <div className="mt-2 space-y-2 text-emerald-900">
          <p>
            <span className="font-semibold">RIASEC Code:</span>{" "}
            {types.join("") || "-"}
          </p>
          <p>
            <span className="font-semibold">Top Types:</span>{" "}
            {types.length
              ? types
                  .map((type) => `${type} - ${labels[type] || type}`)
                  .join(", ")
              : "-"}
          </p>
          {result.scores && (
            <div>
              <p className="font-semibold">Scores:</p>
              <div className="mt-1 grid grid-cols-2 gap-1">
                {Object.entries(result.scores).map(([type, score]) => (
                  <span
                    key={type}
                    className="rounded bg-white/70 px-2 py-1 text-xs"
                  >
                    {type} - {labels[type] || type}: {String(score)}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            {(["fields", "courses", "careers"] as const).map((key) => (
              <div key={key}>
                <p className="font-semibold">
                  {key[0].toUpperCase() + key.slice(1)}:
                </p>
                <List items={suggested[key]} />
              </div>
            ))}
          </div>
          <p>
            <span className="font-semibold">Completed At:</span>{" "}
            {result.completedAt
              ? toDdMmYyyy(result.completedAt) || result.completedAt
              : "-"}
          </p>
        </div>
      )}
    </div>
  );
};
export default CareerTestSection;
